import {
  ModuleCategory,
  SchemaTypeHelper,
  SubscribesTo,
  createComponentAdmin,
} from '@backstage-components/base';
import {Static, Type} from '@sinclair/typebox';

export const reactName = 'Slideshow';
export const name = reactName;
export const description = reactName;
const category: ModuleCategory = 'custom';

const schema = Type.Object({
  images: Type.Array(Type.String({title: 'Image'}), {
    title: 'Image Slides',
    description: 'Click "Add Item" to start adding images to your slideshow.',
    maxItems: 30,
    minItems: 1,
  }),
  interactive: Type.Optional(
    Type.Boolean({
      title: 'Interactive',
      default: true,
      description:
        'Whether clicking on the slideshow advances to the next image',
    })
  ),
  imageDuration: Type.Number({
    title: 'Image Duration',
    default: 8,
    description:
      'Duration an image is shown, in seconds, before transitioning.',
    minimum: 1,
    maximum: 20,
    multipleOf: 1,
  }),
  transitionDuration: Type.Number({
    title: 'Transition Duration',
    description: 'Duration, in seconds, of the transition between images.',
    default: 1.5,
    minimum: 0.1,
    maximum: 5,
    multipleOf: 0.1,
  }),
  maxZoom: Type.Number({
    title: 'Zoom',
    description:
      'Maximum amount the image can zoom in as part of the pan & zoom animation.',
    default: 1.5,
    minimum: 1,
    maximum: 3,
    multipleOf: 0.1,
  }),
  grain: Type.Number({
    title: 'Grain',
    description:
      'Grain effect amount. Grain is animated pixels over the image.',
    default: 0.3,
    minimum: 0,
    maximum: 1,
    multipleOf: 0.01,
  }),
  vignette: Type.Number({
    title: 'Vignette',
    description:
      'Vignette effect amount. Vignette is an inner shadow around the edges of the frame.',
    default: 0.5,
    minimum: 0,
    maximum: 1,
    multipleOf: 0.01,
  }),
  bgColor: Type.String({
    title: 'Background Color',
    default: '#000',
    description:
      'The slideshow background color (visible when images are transitioning).',
  }),
  tintColor: Type.String({
    title: 'Tint Color',
    default: '#000',
    description: 'Color to tint images',
  }),
  tintAmount: Type.Number({
    title: 'Tint Amount',
    description: 'Amount to tint images',
    default: 0,
    minimum: 0,
    maximum: 1,
    multipleOf: 0.01,
  }),
  audioURL: Type.Optional(
    Type.String({
      title: 'Audio URL',
      default: '',
      description: 'URL of an MP3 to play during the slideshow (OPTIONAL).',
    })
  ),
  audioVolume: Type.Number({
    title: 'Audio Volume',
    description: 'Volume level for  the Audio',
    default: 1,
    minimum: 0,
    maximum: 1,
    multipleOf: 0.01,
  }),
  audioLoop: Type.Optional(
    Type.Boolean({
      title: 'Loop Audio',
      default: true,
      description: 'Whether to loop the audio.',
    })
  ),
  initMuted: Type.Optional(
    Type.Boolean({
      title: 'Initialize Muted',
      default: false,
      description: 'Whether to mute the audio on load',
    })
  ),
});

const uiSchema = {
  'ui:groups': {
    'ui:template': 'tabs',
    sections: [
      [
        'Properties',
        [
          {
            'ui:template': 'accordion',
            sections: [
              ['Slides', ['images']],
              [
                'Settings',
                [
                  'interactive',
                  'imageDuration',
                  'transitionDuration',
                  'maxZoom',
                ],
              ],
              [
                'Appearance',
                ['grain', 'vignette', 'tintAmount', 'bgColor', 'tintColor'],
              ],
              ['Audio', ['audioURL', 'audioVolume', 'audioLoop', 'initMuted']],
            ],
          },
        ],
      ],
      [
        'Styling',
        [
          {
            'ui:template': 'accordion',
            sections: [['Custom Styles', ['styleAttr']]],
          },
        ],
      ],
      ['Animations', []],
    ],
  },
  images: {
    items: {
      'ui:widget': 'AssetSelectWidget',
      'ui:options': {
        buttonTitle: 'Select Asset',
      },
    },
  },
  grain: {
    'ui:widget': 'range',
    'ui:options': {
      type: 'percentage',
    },
  },
  vignette: {
    'ui:widget': 'range',
    'ui:options': {
      type: 'percentage',
    },
  },
  tintAmount: {
    'ui:widget': 'range',
    'ui:options': {
      type: 'percentage',
    },
  },
  bgColor: {
    'ui:widget': 'color',
  },
  tintColor: {
    'ui:widget': 'color',
  },
  audioVolume: {
    'ui:widget': 'range',
    'ui:options': {
      type: 'percentage',
    },
  },
};

const defaultFieldData: Static<typeof schema> = {
  images: [],
  interactive: true,
  imageDuration: 8,
  transitionDuration: 1.5,
  grain: 0.5,
  vignette: 0.5,
  maxZoom: 1.5,
  tintAmount: 0,
  bgColor: '#000',
  tintColor: '#000',
  audioURL: '',
  audioVolume: 1,
  audioLoop: true,
  initMuted: false,
};

const instructions = Type.Union([
  SubscribesTo({
    topic: `${reactName}:toggleVisibility`,
    description: 'Toggles Visibility and render loop.',
  }),
  SubscribesTo({
    topic: `${reactName}:show`,
    description: 'Shows the slideshow module.',
  }),
  SubscribesTo({
    topic: `${reactName}:hide`,
    description: 'Hides the slideshow module.',
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: 'fe62294b-8feb-4527-a812-4476a603754e',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData,
  slotConfiguration: {},
  schema,
  uiSchema,
  instructions,
  category,
})
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
